import { Subscription, SUBSCRIPTION_NAMES } from '@innovyze/shared-utils';
import { PageWrapper } from '@innovyze/stylovyze';
import { useFormikContext } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { bulkUpdateSingleSubscriptionUsers } from '@Actions/users';
import { ActionButton, CancelButton } from '@Components/Buttons';
import ManageUserFields from '@Components/ManageUserFields';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { MAIN_ROUTES } from '@Types/routes';
import { ManageUsersFormikContext } from '@Types/users.types';
import {
	useGetExternalUsersFromPaginated,
	useGetInternalUsersFromPaginated,
	validateUsersLimit,
} from '@Utils/parseUsersUtil';
import {
	Card,
	Container,
	Fields,
	SecondaryContainer,
} from './ManageUser.styles';
import ManageUsersCards from './ManageUsersCards';
import getUsersToAddRemove from './utils/getUsersToAddRemove';

const ManageUsers = (): JSX.Element => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();

	const { values } = useFormikContext<ManageUsersFormikContext>();
	const { subscriptionId } = useParams<{ subscriptionId: Subscription }>();

	const { initialized: productsInitialized, list: products } =
		useSelector(getValue).products;
	const {
		paginatedUsers: { initialized: UsersInitialized, users: originalUsers },
		bulkUser: { status },
	} = useSelector(getValue).users;

	const externalUsers = useGetExternalUsersFromPaginated();
	const internalUsers = useGetInternalUsersFromPaginated();

	const subscription = products.find(
		product => product.name === subscriptionId,
	);

	const newExternalUsers = externalUsers.filter(el =>
		values.externalUsers.includes(el.id),
	);
	const newInternalUsers = internalUsers.filter(el =>
		values.internalUsers.includes(el.id),
	);

	const { limitSurpassed: externalLimitSurpassed } = validateUsersLimit(
		newExternalUsers.map(el => el.id),
		subscription,
	);

	const handleSave = () => {
		const userOperations = getUsersToAddRemove(
			originalUsers,
			[...values.externalUsers, ...values.internalUsers],
			subscription?.name || '',
		);
		dispatch(bulkUpdateSingleSubscriptionUsers(userOperations));
	};

	const initialized = UsersInitialized && productsInitialized;

	return (
		<PageWrapper
			title={SUBSCRIPTION_NAMES[subscriptionId]}
			applyPadding={true}
			isLoading={!initialized}
			secondary={
				<SecondaryContainer>
					<CancelButton
						data-cy="cancelButton"
						style={{ marginLeft: 8 }}
						onClick={() => {
							history.push(MAIN_ROUTES.UserManagement);
						}}>
						{t('Cancel')}
					</CancelButton>
					<ActionButton
						data-cy="saveButton"
						disabled={
							externalLimitSurpassed || status === 'pending'
						}
						onClick={handleSave}
						color="primary">
						{t('Save')}
					</ActionButton>
				</SecondaryContainer>
			}
			headerUnderline>
			<Container>
				<Fields>
					<ManageUserFields product={subscription} />
				</Fields>
				<Card>
					<ManageUsersCards
						subscription={subscription}
						newExternalUsers={newExternalUsers}
						newInternalUsers={newInternalUsers}
					/>
				</Card>
			</Container>
		</PageWrapper>
	);
};

export default ManageUsers;
